/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup, FormText, Input, Label } from 'reactstrap';
import G17Client from '../../../services/G17Client';
import { addSiteAlert, SiteAlertColors } from '../../../slice/siteAlertsSlice';
import { isValidEmail } from '../../../utils';
import { BlockingLoader } from '../../loader/BlockingLoader';
import { RequestToJoinData } from '../../onboarding/types';
import { BaseStepCompProps } from '../types';

const initialState: RequestToJoinData = {
  initiativeIds: [],
  firstName: '',
  lastName: '',
  email: '',
  message: ''
}

interface RequestToJoinFormProps extends BaseStepCompProps {
  initiativeId: string;
}

export const RequestToJoinForm = (props: RequestToJoinFormProps) => {
  const { formData, handlePrevious, initiativeId, appConfig } = props;

  const dispatch = useDispatch();
  const [requestForm, setRequestForm] = useState<RequestToJoinData>({ ...initialState, initiativeIds: [initiativeId] });
  const [isLoading, setLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const isEmailValid = isValidEmail(requestForm.email);
  const requiredFields: (keyof RequestToJoinData)[] = ['firstName', 'lastName', 'email'];
  const isFormValid = requiredFields.every(field => requestForm[field]) && isEmailValid;

  const handleChange = (field: keyof RequestToJoinData, value: string) => {
    setRequestForm((prevData) => ({
      ...prevData,
      [field]: value
    }));
  }

  const handleSendRequest = () => {
    if (!isFormValid || !initiativeId || !appConfig) {
      return;
    }
    setLoading(true);
    G17Client.sendOnboardingRequestToJoin(requestForm, appConfig.rootAppPath)
      .then(() => {
        setLoading(false);
        setRequestSent(true);
      })
      .catch((e) => {
        setLoading(false);
        dispatch(addSiteAlert({
          content: e.message ?? 'Could not send request to join. Please try again',
          color: SiteAlertColors.Danger,
        }));
      });
  }

  if (!initiativeId) {
    return (
      <div className='d-lg-flex justify-content-between onboarding-form'>
        <aside className='title-container'>
          <div className='h1 mb-3'>Error</div>
          <p>Something went wrong, please press the Back button and try again.</p>
          <div className='mt-3 d-flex'>
            <Button outline={true} size='sm' className='mr-auto' onClick={handlePrevious}><i className='far fa-angle-left mr-2' />Back</Button>
          </div>
        </aside>
      </div>
    );
  }

  if (requestSent) {
    return (
      <div className='onboarding-form'>
        <div className='h2 mb-3'>Request Sent</div>
        <p>A request to join <strong>{formData.initiativeName}</strong> has now been sent to the Owner of this account.</p>
        <p>You will receive an email when access has been granted, and then you can login using your email address.</p>
      </div>
    );
  }

  return (
    <div className='d-lg-flex justify-content-between onboarding-form'>
      {isLoading ? <BlockingLoader /> : null}
      <aside className='title-container'>
        <div className='h1 mb-3'>Company already onboard</div>
        <p>
          <u>
            <a href='mailto:support@g17.eco' className='text-ThemeHeadingLight'>
              Contact us if you think this is a mistake
            </a>
          </u>
        </p>
      </aside>
      <section className='form-container mt-5'>
        <div className='strong mb-2'>Company already onboard</div>
        <p>Do you want to request access? A request with your email address,
          and message, will be sent to the Owner of the account.</p>

        <div className='strong mt-4'>{formData.initiativeName}</div>

        <Form className='mt-3 mb-5'>
          <div className='row'>
            <div className='col-6'>
              <FormGroup>
                <Label for='firstName'>Your First Name</Label>
                <Input
                  placeholder='First name'
                  valid={requestForm.firstName.length > 0}
                  type='text'
                  required
                  name='firstName'
                  defaultValue={requestForm.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                />
              </FormGroup>
            </div>
            <div className='col-6'>
              <FormGroup>
                <Label for='lastName'>Your Last Name</Label>
                <Input
                  placeholder='Last name'
                  valid={requestForm.lastName.length > 0}
                  type='text'
                  required
                  name='lastName'
                  defaultValue={requestForm.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                />
              </FormGroup>
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <FormGroup>
                <Label for='email'>Your Email</Label>
                <Input
                  placeholder='Email'
                  name='email'
                  type='email'
                  invalid={requestForm.email.length > 0 && !isEmailValid}
                  valid={requestForm.email.length > 0 && isEmailValid}
                  required
                  defaultValue={requestForm.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
              </FormGroup>
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <FormGroup>
                <Label for='message'>
                  Message to account owner / reason for request (optional)
                </Label>
                <Input
                  name='message'
                  placeholder='Reason for request'
                  type='textarea'
                  maxLength={240}
                  rows={4}
                  defaultValue={requestForm.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                />
                <FormText className='text-right'>{(requestForm.message ?? '').length}/240 chars</FormText>
              </FormGroup>
            </div>
          </div>
        </Form>
        <div className='mt-3 d-flex'>
          <Button outline={true} onClick={handlePrevious}><i className='far fa-angle-left mr-2' />Back</Button>
          <Button color='primary' className='ml-auto' disabled={!isFormValid} onClick={handleSendRequest}>Request to join</Button>
        </div>
      </section>
    </div>
  );
}
